/**
 * Versions:
 * 0.01 (9.12.2019)
 * 
 * 0.02 (20.1.2020)
 * - pridano deltaS
 */

import { NodeList } from "../Collections/NodeList";
import { Pool } from "../Collections/Pool";
import { Node } from "../Collections/Node";
import { TimeManager } from "./TimeManager";



export class Timer {
    /**
     * (RO) Spravce casu, kteremu casovac patri
     */
    get manager(): TimeManager { return this._manager; }
    private _manager: TimeManager;

    /**
     * (RO) Elapsed time in ms
     */
    get time(): number { return this._time; }
    private _time: number;

    /**
     * (RO) Pomer vuci pozadovane rychlosti hry. 1 = 100% rychlost, 2 = 50% rychlost, ...
     * Pokud casovac nebezi tak 0.
     */
    get deltaRatio(): number {
        if ((this._flags & eFlag.started) != 0)
            return this._manager.deltaRatio;

        return 0;
    }

    /**
     * (RO) Cas v ms od posledniho snimku. Pokud casovac nebezi, tak 0.
     */
    get deltaMs(): number {
        if ((this._flags & eFlag.started) != 0)
            return this._manager.deltaMs;

        return 0;
    }

    /**
     * (RO) Cas ve vterinach od posledniho snimku. Pokud casovac nebezi, tak 0.
     */
    get deltaS(): number {
        if ((this._flags & eFlag.started) != 0)
            return this._manager.deltaS;

        return 0;
    }

    private _flags: number;

    private _freeTimers: Pool<Timer>;
    private _actTimers: NodeList<Timer>;
    private _inactTimers: NodeList<Timer>;
    private _listNode: Node<Timer>;

    /**
     * (RO) True, if the timer has been started
     */
    get started(): boolean { return (this._flags & eFlag.started) != 0; }

    /**
     * (RO) True, if the started timer has been stopped
     */
    get paused(): boolean { return (this._flags & eFlag.paused) != 0; }

    //-------------------------------------------------------------------------
    /**
     * Constructor. New Timer object is created by TimeManager.createTimer()
     * @param manager
     * @param freeTimers
     * @param actTimers 
     */
    //-------------------------------------------------------------------------
    constructor(manager: TimeManager, freeTimers: Pool<Timer>, actTimers: NodeList<Timer>, inactTimers: NodeList<Timer>) {
        this._manager = manager;

        this._freeTimers = freeTimers;
        this._actTimers = actTimers;
        this._inactTimers = inactTimers;

        this._time = 0;
        this._flags = eFlag.uninitialized;
    }

    //-------------------------------------------------------------------------
    /**
     * Vola TimeManager.
     */
    //-------------------------------------------------------------------------
    _initialize(): Timer {
        this._flags = 0;
        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Vola TimeManager.
     * @param deltaMs 
     */
    //-------------------------------------------------------------------------
    _update(deltaMs: number): void {
        this._time += deltaMs;
    }

    //-------------------------------------------------------------------------
    /**
     * Spusteni casovace.
     * Pokud casovac uz bezi, tak ho vynuluje.
     * Pokud je casovac pozastaven, tak ho vynuluje a spusti 
     */
    //-------------------------------------------------------------------------
    start(): Timer {
        this.checkValidity(false);

        this._time = 0;

        if ((this._flags & eFlag.started) == 0 || (this._flags & eFlag.paused) != 0) {
            if ((this._flags & eFlag.paused) != 0)
                this._inactTimers.removeByNode(this._listNode);

            this._actTimers.add(this);
            this._listNode = this._actTimers.lastNode;

            this._flags |= eFlag.started;
            this._flags &= ~eFlag.paused;
        }

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Pozastaveni casovace. Pokud je jiz pozastaven, tak se nic nestane.
     */
    //-------------------------------------------------------------------------
    pause(): Timer {
        this.checkValidity(true);

        if ((this._flags & (eFlag.started | eFlag.paused)) == eFlag.started) {
            this._flags |= eFlag.paused;
            this._actTimers.removeByNode(this._listNode);
            this._inactTimers.add(this);
            this._listNode = this._inactTimers.lastNode;
        }

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Obnoveni behu pozastaveneho casovace. Pokud neni pozastaven, tak se nic nestane.
     */
    //-------------------------------------------------------------------------
    resume(): Timer {
        this.checkValidity(true);

        if ((this._flags & eFlag.paused) != 0) {
            this._flags &= ~eFlag.paused;

            this._inactTimers.removeByNode(this._listNode);
            this._actTimers.add(this);
            this._listNode = this._actTimers.lastNode;
        }

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Uvolneni objektu casovace pro jiny casovas.
     * Uvolneny casovac neni mozne dale pouzivat (dojde k jeho vraceni do poolu volnych casovacu).
     */
    //-------------------------------------------------------------------------
    release(): void {
        this.checkValidity(false);

        // Odebrat casovac ze seznamu bezicich casovacu
        if (this._listNode) {
            if ((this._flags & eFlag.paused) == 0) {
                this._actTimers.removeByNode(this._listNode);
            }
            else {
                this._inactTimers.removeByNode(this._listNode);
            }

            this._listNode = undefined;
        }

        // Nastavit casovac jako neinicializovany
        this._time = 0;
        this._flags = eFlag.uninitialized;

        // Vratit casovac do poolu volnych casovacu
        this._freeTimers.returnItem(this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private checkValidity(reqStarted: boolean): void {
        if ((this._flags & eFlag.uninitialized) != 0)
            throw new Error("Timer has not been initialized!");

        if (reqStarted && (this._flags & eFlag.started) == 0)
            throw new Error("Timer has not been started yet!");
    }
}



const enum eFlag {
    started = 1,
    paused = 2,
    uninitialized = 4
}